import { useRef, useState, useCallback } from "react";
import { IonIcon, IonButton } from "@ionic/react";
import {
  arrowBackOutline,
  createOutline,
  cloudUploadOutline,
  openOutline,
} from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "@tanstack/react-query";
import httpService from "../../services/httpService";
import { api_s2, portalUrl } from "../../config";
import { usePymbleApi } from "../../hooks/usePymbleApi";
import { displayMsg } from "pymble-ui/components";
import type { GroupDetail } from "../../hooks/useGroupDetail";

import "./GroupHeader.css";

interface GroupHeaderProps {
  group: GroupDetail;
  memberCount: number;
  isAdmin?: boolean;
}

const ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/webp";
const ACCEPTED_MIME_SET = new Set(ACCEPTED_IMAGE_TYPES.split(","));

const GroupHeader: React.FC<GroupHeaderProps> = ({
  group,
  memberCount,
  isAdmin = false,
}) => {
  const history = useHistory();
  const { instance } = useMsal();
  const { callApi } = usePymbleApi();
  const queryClient = useQueryClient();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [localCoverUrl, setLocalCoverUrl] = useState<string | null>(null);
  const dragCounter = useRef(0);

  // CoverImage can be a FileMetadata object (new) or a URL string (legacy)
  const serverCoverUrl =
    typeof group.CoverImage === "object" && group.CoverImage !== null
      ? (group.CoverImage as { Url?: string }).Url
      : typeof group.CoverImage === "string"
        ? group.CoverImage
        : null;

  // Use local preview URL immediately after upload, fall back to server URL
  const coverUrl = localCoverUrl || serverCoverUrl;

  const coverBg = coverUrl
    ? `url(${coverUrl})`
    : "linear-gradient(135deg, #002f67 0%, #1a5fa0 50%, #3a7fd5 100%)";

  const uploadCoverFile = useCallback(
    async (file: File) => {
      if (uploading) return;
      if (!ACCEPTED_MIME_SET.has(file.type)) {
        displayMsg("Please use a JPEG, PNG, or WebP image", {
          type: "warning",
          position: "top",
          duration: 2000,
        });
        return;
      }

      setUploading(true);
      try {
        await instance.initialize();
        const accounts = instance.getAllAccounts();
        const account = accounts[0];
        if (!account) throw new Error("Not authenticated");

        const tokenResult = await instance.acquireTokenSilent({
          scopes: api_s2.scopes,
          account,
        });

        const formData = new FormData();
        formData.append("file", file);

        const params = new URLSearchParams();
        params.set("container", "assets");
        params.set("collection", "Group");
        params.set("id", group._id);
        params.set("origin", "azure");
        params.set("secure", "false");
        params.set("private", "false");
        params.set("tags", "group-cover");

        const uploadRes = await httpService.post(
          `/storage/file/?${params.toString()}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${tokenResult.accessToken}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const fileData = uploadRes?.data?.File;
        if (!fileData) throw new Error("Upload failed — no file data returned");

        // Show the new image immediately (optimistic)
        const newUrl = fileData.Url || fileData.url;
        if (newUrl) setLocalCoverUrl(newUrl);

        await callApi("PUT", `/group/admin/${group._id}`, {
          data: { _id: group._id, CoverImage: fileData },
        });

        queryClient.invalidateQueries({
          queryKey: ["group-detail", group._id],
        });
        displayMsg("Cover image updated", {
          type: "success",
          position: "top",
          duration: 1500,
        });
      } catch {
        displayMsg("Failed to upload cover image", {
          type: "danger",
          position: "top",
          duration: 3000,
        });
      } finally {
        setUploading(false);
        if (coverInputRef.current) coverInputRef.current.value = "";
      }
    },
    [instance, callApi, group._id, queryClient, uploading]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) uploadCoverFile(file);
    },
    [uploadCoverFile]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      if (isAdmin && e.dataTransfer.items.length > 0) {
        setDragOver(true);
      }
    },
    [isAdmin]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      dragCounter.current = 0;
      if (isAdmin && !uploading && e.dataTransfer.files.length > 0) {
        uploadCoverFile(e.dataTransfer.files[0]);
      }
    },
    [isAdmin, uploading, uploadCoverFile]
  );

  return (
    <div className="group-header">
      <div
        className={`group-header-cover${dragOver ? " cover-drag-over" : ""}`}
        style={{ backgroundImage: coverBg }}
        onDragEnter={isAdmin ? handleDragEnter : undefined}
        onDragLeave={isAdmin ? handleDragLeave : undefined}
        onDragOver={isAdmin ? handleDragOver : undefined}
        onDrop={isAdmin ? handleDrop : undefined}
      >
        <div className="group-header-back">
          <IonButton
            fill="clear"
            color="light"
            onClick={() => history.push("/groups")}
            className="ion-text-capitalize"
          >
            <IonIcon slot="start" icon={arrowBackOutline} />
            Groups
          </IonButton>
        </div>

        {isAdmin && (
          <button
            className="cover-edit-btn"
            onClick={() => coverInputRef.current?.click()}
            disabled={uploading}
            title="Change cover image"
          >
            <IonIcon icon={createOutline} />
          </button>
        )}

        {dragOver && isAdmin && (
          <div className="cover-drop-overlay">
            <IonIcon icon={cloudUploadOutline} />
            <span>Drop image to set as cover</span>
          </div>
        )}

        <input
          ref={coverInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          className="cover-file-input"
          onChange={handleFileInput}
        />
      </div>

      <div className="group-header-info">
        <div className="group-header-icon-wrap">
          <img
            src={group.Icon}
            alt={group.Name?.Short || "Group"}
            className="group-header-icon"
          />
        </div>

        <div className="group-header-details">
          <h1 className="group-header-name">{group.Name?.Short}</h1>
          {group.Description?.Short && (
            <p className="group-header-tagline">{group.Description.Short}</p>
          )}
        </div>

        <div className="group-header-actions">
          <span className="group-header-member-count">
            <strong>{memberCount}</strong> Members
          </span>
          <IonButton
            fill="outline"
            color="secondary"
            className="ion-text-capitalize"
            href={`${portalUrl}/groups/${group._id}`}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
          >
            View live group
            <IonIcon slot="end" icon={openOutline} />
          </IonButton>
        </div>
      </div>
    </div>
  );
};

export default GroupHeader;
